import RepositoryBase from "./repositoryBase";
import EshopRequestBuilder from "../data/eshopRequestBuilder";
import AuthorizationService from "../services/authorizationService";
import EventBus from "../services/eventBus";
import GooglePayMetadata from "../models/googlePayMetadata";
import { plainToClass } from "class-transformer";
import { paymentGateway } from "../config";
import EshopError from "../models/eshopError";
import * as Sentry from "@sentry/react";
import GooglePayInitPaymentResponse from "../models/googlePayInitPaymentResponse";
import GooglePayProcessPaymentRequest from "models/googlePayProcessPaymentRequest";
import GooglePayProcessPaymentResponse from "models/googlePayProcessPaymentResponse";

export const GET_GOOGLE_PAY_INFO_REQUEST_FAILURE = "GET_GOOGLE_PAY_INFO_REQUEST_FAILURE";
export const GET_GOOGLE_PAY_TRANSACTION_STATUS_CHECK_FAILURE = "GET_GOOGLE_PAY_TRANSACTION_STATUS_CHECK_FAILURE";
export const SUBMIT_GOOGLE_PAY_PAYMENT_FAILURE = "SUBMIT_GOOGLE_PAY_PAYMENT_FAILURE";
export const SUBMIT_GOOGLE_PAY_PAYMENT_CONFIRMATION_FAILURE = "SUBMIT_GOOGLE_PAY_PAYMENT_CONFIRMATION_FAILURE";

export default class PaymentRepository extends RepositoryBase<EshopRequestBuilder> {
  constructor(
    protected apiFactory: ReturnType<typeof EshopRequestBuilder.Factory>,
    protected authorizationService: AuthorizationService,
    protected eventBus: EventBus
  ) {
    super(() => apiFactory(), authorizationService, eventBus);
  }

  getGooglePayMetadata() {
    return this.callApi(() =>
      this.apiFactory()
        .customApiUrlWithVersion(paymentGateway, 1)
        .all("paygate")
        .all("google_pay")
        .all("metadata")
        .get<GooglePayMetadata>()
        .then(googlePayMetadata => plainToClass(GooglePayMetadata, googlePayMetadata))
        .catch(error => {
          Sentry.captureException(error);

          return new EshopError(GET_GOOGLE_PAY_INFO_REQUEST_FAILURE);
        })
    );
  }

  submitGooglePayment(orderId: string, redirectAfterConfirmationUrl: string, payload: google.payments.api.PaymentData) {
    return this.callApi(() =>
      this.apiFactory()
        .customApiUrlWithVersion(paymentGateway, 1)
        .all("paygate")
        .all("google_pay")
        .one("charge_orders", orderId)
        .all("payment")
        .post<GooglePayInitPaymentResponse>({
          payload: btoa(payload.paymentMethodData.tokenizationData.token),
          redirectAfterConfirmationUrl: redirectAfterConfirmationUrl,
        })
        .catch(error => {
          Sentry.captureException(error);

          return new EshopError(SUBMIT_GOOGLE_PAY_PAYMENT_FAILURE);
        })
    );
  }

  submitGooglePayPaymentConfirmation(orderId: string, connectionId: string, payload: GooglePayProcessPaymentRequest) {
    return this.callApi(() =>
      this.apiFactory()
        .customApiUrlWithVersion(paymentGateway, 1.1)
        .all("paygate")
        .all("google_pay")
        .one("charge_orders", orderId)
        .all("payment_confirmation")
        .post<GooglePayProcessPaymentResponse>(payload, undefined, connectionId)
        .catch(error => {
          Sentry.captureException(error);

          return new EshopError(SUBMIT_GOOGLE_PAY_PAYMENT_CONFIRMATION_FAILURE);
        })
    );
  }

  getGooglePayTransactionStatus(orderId: string) {
    return this.callApi(() =>
      this.apiFactory()
        .customApiUrlWithVersion(paymentGateway, 1)
        .all("paygate")
        .all("google_pay")
        .one("charge_order_payments", orderId)
        .get<GooglePayProcessPaymentResponse>()
        .catch(error => {
          Sentry.captureException(error);

          return new EshopError(GET_GOOGLE_PAY_TRANSACTION_STATUS_CHECK_FAILURE);
        })
    );
  }
}
